import { FC, Fragment, ReactElement, ReactNode, useEffect } from "react";
import { observer } from "mobx-react";
import { TMessage } from "@/i18n/tt";
import { Box, Card, Grid, GridItem, VBox } from "@/components-v2/Box";
import { Logo } from "@/components-v2/Logo";
import { portfolioUrl } from "@/utils/navigation";
import { formatOrgNum, removeTitleAndTD } from "@/utils/other";
import { DocumentPreview } from "@/components-v2/DocumentPreview";
import { UploadedAttachment } from "@/fileUpload/fileUploadDomain";
import {
  BackButton,
  Button,
  ButtonProps,
  IconButton,
  LinkButton,
  ToggleButton,
} from "@/components-v2/Button";
import { CompanyItem, LocalizedTextForDocument, RenderedDecisionRow, SignerType } from "./common";
import { Alert, AlertProps } from "@/components-v2/Alert";
import { Caption } from "@/components-v2/Caption";
import { downloadFile } from "@/api";
import { DropdownMenu } from "@/components-v2/Dropdown";
import { isMobileView } from "@/utils/screen";
import { formatDateTime, formatTime } from "@/i18n/dateFormat";
import { isNotBlank } from "@/utils/strings";
import parse from "html-react-parser";
import { Language } from "@/i18n/languages";
import { Tag, TagProps } from "@/components-v2/Tag";
import { accessColors, AccessType } from "@/types/enums/AccessType";
import { CustomRole } from "./customRole";
import { accessTypeLabel } from "@/portfolio/contacts/customRoleDomain";
import { Icon } from "@/components-v2/Icon";
import { compact } from "lodash";
import { AttachmentsPanel } from "@/components-v2/AttachmentsPanel";
import { Markdown } from "@/text/Markdown";
import { DocumentType } from "./documents";
import { DCCallback, useLanguage, useRootStore, useTranslation } from "@/stores/RootStoreContext";
import { Dialog } from "@/components-v2/Dialog";
import { Dismissable } from "@/components-v2/Dismissable";
import { IconName } from "@/components-v2/iconMapping";
import { Checkbox, MCheckboxGroup } from "@/components-v2/Checkbox";
import Form from "@/components-v2/Form";
import { MTextInput } from "@/components-v2/TextInput";
import {
  CommonSigningProperties,
  CompaniesForPreview,
  CompanyFolderEntry,
  DestinationFolderMode,
  DocumentArchiveDestination,
  DocumentsForPreview,
  DocumentStorageDestination,
  OptionFieldModel,
  QuickAddSignerItems,
  RadioFieldModel,
  SealedCopyAttachment,
  SendSealedCopyToOtherModel,
  SharepointDestinationModel,
  SignerModel,
  SignersForm,
  StringFieldModel,
} from "./commonForms";
import { Table } from "@/components-v2/Table";
import { when } from "@/utils/functions";
import { MRadioButtonGroup } from "@/components-v2/RadioButton";
import { MSelect, Select } from "@/components-v2/Select";
import { preventDefault, stopPropagation } from "@/utils/events";
import { InfoButton } from "@/components-v2/InfoButton";
import { FullScreenLayout } from "@/components-v2/FullScreenLayout";
import { HiddenForMobile } from "@/components-v2/Mobile";
import { FilePreview, FilePreviewProps } from "@/components-v2/FilePreview";
import { LocalizedTextEditor } from "@/text/LocalizedTextEditor";
import { ConfigureSharepointDestination } from "@/sharepoint/sharepointComponents";
import { TMessageKey } from "@/i18n/types.gen";
import { formatUserName } from "@/types/UserInfo";
import { getActionLabel } from "@/archive/commonArchiveDomain";
import { DestinationFolder, getEntryLabel } from "@/archive/archiveDomain";
import { DynamicRecipientOption, RecipientsEditModel } from "@/dbot/summary/summaryDomain";
import { dynamicRecipientLabels, SendDocumentForViewingModel } from "@/dbot/common/commonDomain";
import { LoadingIndicator } from "@/components-v2/LoadingIndicator.tsx";

export const LogoWithCompanyName: FC<{
  companyName?: string;
  orgNo?: string;
  backUrl?: string;
}> = observer(({ companyName, orgNo, backUrl }) => {
  const history = useRootStore().history;
  return (
    <Box alignItems={"center"} overflow={"hidden"}>
      {backUrl ? (
        <BackButton onClick={() => history.push(backUrl)} />
      ) : (
        <Logo linkUrl={portfolioUrl} />
      )}
      <VBox gap={false} overflow={"hidden"}>
        <strong className={"truncate"}>{companyName}</strong>
        <small className={"subtle truncate"}>{formatOrgNum(orgNo)}</small>
      </VBox>
    </Box>
  );
});

export const MeetingDocumentView: FC<{
  content?: string;
  uploadedAttachments?: UploadedAttachment[];
}> = observer(({ content, uploadedAttachments }) => (
  <DocumentPreview
    content={[{ type: "document", documentId: "n/a", content }]}
    centered={false}
    padding={false}
    uploadedAttachments={uploadedAttachments}
  />
));

export const AttachmentsCard: FC<{
  attachments: UploadedAttachment[];
  onDownload: (fileId: string) => void;
}> = observer(({ attachments, onDownload }) => {
  const { t } = useTranslation();
  return (
    <Card title={t("attachments")}>
      {attachments.length === 0 && <div className={"subtle"}>{t("no-attachments")}</div>}
      <ul>
        {attachments.map((attachment) => (
          <li key={attachment.id} className="flex">
            <LinkButton
              tooltip={attachment.filename}
              icon={{ name: "paperclip" }}
              weight={"normal"}
              label={attachment.filename}
              onClick={() => onDownload(attachment.id)}
              truncate
            />
          </li>
        ))}
      </ul>
    </Card>
  );
});

export type DownloadAsPdfButtonProps = {
  documentType?: DocumentType;
  isDownloading?: boolean;
  onCancel?: DCCallback;
  onConfirm?: DCCallback;
  onDownload: DCCallback;
  onDownloadWithAttachments?: DCCallback;
  variant?: "link" | "secondary" | "icon";
};

export const DownloadAsPdfButton: FC<DownloadAsPdfButtonProps> = (props) => {
  const { t } = useTranslation();
  const variant = props.variant ?? "secondary";

  const isNotice =
    props.documentType === DocumentType.notice_general_meeting ||
    props.documentType === DocumentType.notice_board_meeting;

  const isMinutes =
    props.documentType === DocumentType.general_meeting ||
    props.documentType === DocumentType.board_meeting;

  return (
    <>
      {props.onConfirm && (
        <Dialog
          title={{ key: "download-non-pdf-attachment.title" }}
          onCancel={props.onCancel}
          onSubmit={props.onConfirm}
          submitLabel={{ key: "download-non-pdf-attachment.download" }}
          isSubmitting={props.isDownloading}
        >
          <div>{t("download-non-pdf-attachment.warning")}</div>
        </Dialog>
      )}
      {props.onDownloadWithAttachments ? (
        <DropdownMenu
          button={
            variant === "secondary"
              ? {
                  type: "button",
                  variant: "secondary",
                  label: { key: "download-pdf" },
                  loading: !props.onConfirm && props.isDownloading,
                }
              : variant === "icon"
                ? {
                    type: "icon",
                    icon: "download",
                    tooltip: { key: "download-pdf" },
                  }
                : {
                    type: "link",
                    label: { key: "download-pdf" },
                    loading: !props.onConfirm && props.isDownloading,
                  }
          }
          items={[
            {
              type: "item",
              icon: "download",
              label: isNotice
                ? { key: "download-as-pdf.notice-and-attachments" }
                : isMinutes
                  ? { key: "download-as-pdf.minutes-and-attachments" }
                  : { key: "download-as-pdf.document-and-attachments" },
              onSelect: props.onDownloadWithAttachments,
            },
            {
              type: "item",
              icon: "download",
              label: isNotice
                ? { key: "download-as-pdf.notice-only" }
                : isMinutes
                  ? { key: "download-as-pdf.minutes-only" }
                  : { key: "download-as-pdf.document-only" },
              onSelect: props.onDownload,
            },
          ]}
        />
      ) : variant === "secondary" ? (
        <Button
          variant={"secondary"}
          loading={props.isDownloading}
          label={{ key: "download-pdf" }}
          icon={"download"}
          onClick={props.onDownload}
        />
      ) : variant === "icon" ? (
        <IconButton
          icon={"download"}
          tooltip={{ key: "download-pdf" }}
          onClick={props.onDownload}
          loading={props.isDownloading}
        />
      ) : (
        <LinkButton
          loading={props.isDownloading}
          label={{ key: "download-pdf" }}
          icon={"download"}
          onClick={props.onDownload}
        />
      )}
    </>
  );
};

export const NoticeActionsView: FC<{
  download: DownloadAsPdfButtonProps;
  addToCalendar?: {
    title: string;
    companyName: string;
    ical: {
      [key in Language]?: string;
    };
    googleCalendar: {
      [key in Language]?: string;
    };
  };
}> = observer(({ download, addToCalendar }) => {
  const { t } = useTranslation();
  return (
    <Card>
      <VBox gap={"md"}>
        {download && <DownloadAsPdfButton {...download} />}
        {addToCalendar && (
          <div>
            <AddToCalendar
              variant={"link"}
              title={t("meeting_at", {
                meeting: addToCalendar.title,
                place: addToCalendar.companyName,
              })}
              ical={addToCalendar.ical}
              googleCalendar={addToCalendar.googleCalendar}
            />
          </div>
        )}
      </VBox>
    </Card>
  );
});

export type AddToCalendarProps = {
  variant?: "link" | "button";
  title: string;
  description?: string;
  meetingStartDate?: string;
  meetingEndDate?: string;
  meetingLocation?: string;
  ical: {
    [key in Language]?: string;
  };
  googleCalendar: {
    [key in Language]?: string;
  };
};

export const AddToCalendar: FC<AddToCalendarProps> = observer(
  ({ variant = "button", title, ical, googleCalendar }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    const defaultIcal = Object.values(ical)[0];
    const defaultGoogleCalendar = Object.values(ical)[0];
    const downloadIcal =
      Boolean(defaultIcal) &&
      (() => {
        downloadFile(
          new Blob([ical[locale] ?? defaultIcal], {
            type: "plain/text",
          }),
          title + ".ics",
        );
      });

    const openGoogleUrl =
      Boolean(defaultGoogleCalendar) &&
      (() => {
        window.open(googleCalendar[locale] ?? defaultGoogleCalendar);
      });
    return (
      <DropdownMenu
        button={
          variant === "button"
            ? {
                type: "button",
                variant: "secondary",
                label: t("add_to_calendar"),
              }
            : {
                type: "link",
                label: t("add_to_calendar"),
                weight: "normal",
                icon: "calendar",
              }
        }
        items={[
          openGoogleUrl && {
            type: "item",
            icon: "google-logo",
            label: "Google",
            onSelect: openGoogleUrl,
          },
          downloadIcal && {
            type: "item",
            icon: "microsoft-outlook-logo",
            label: "Outlook",
            onSelect: downloadIcal,
          },
          downloadIcal && {
            type: "item",
            icon: "apple-logo",
            label: "Calendar",
            onSelect: downloadIcal,
          },
        ]}
      />
    );
  },
);

export const HeaderMeetingInfo: FC<{
  title: string;
  companyName: string;
  meetingStartDate: string;
  meetingEndDate?: string;
  ical?: {
    [key in Language]?: string;
  };
  googleCalendar?: {
    [key in Language]?: string;
  };
  orgNo?: string;
  edit?: () => void;
  isCirculation?: boolean;
  deadlineForFeedback?: string;
  meetingStatus?: TagProps;
  meetingLink?: string;
  actions?: Array<ButtonProps & { key: string }>;
}> = observer(
  ({
    title,
    companyName,
    meetingStartDate,
    meetingEndDate,
    ical,
    googleCalendar,
    edit,
    isCirculation,
    deadlineForFeedback,
    meetingStatus,
    meetingLink,
    actions,
  }) => {
    const { t } = useTranslation();
    const locale = useLanguage();
    return (
      <Box
        justifyContent={isMobileView() ? undefined : "space-between"}
        maxWidth={"xl"}
        vertical={isMobileView()}
      >
        <VBox>
          <h3>
            <Box alignItems={"center"}>
              <span>{title}</span>
              {meetingStatus && <Tag {...meetingStatus} />}
            </Box>
          </h3>
          <Box gap={"md"}>
            {isCirculation ? (
              <span>{t("meeting-is-circulation")}</span>
            ) : (
              compact([
                formatDateTime(locale, meetingStartDate),
                meetingEndDate && formatTime(locale, meetingEndDate),
              ]).join("-")
            )}
            {deadlineForFeedback && (
              <span>
                {t("deadline-for-feedback", { date: formatDateTime(locale, deadlineForFeedback) })}
              </span>
            )}
            {meetingLink && (
              <a
                href={meetingLink.startsWith("http") ? meetingLink : `https://${meetingLink}`}
                target={"_blank"}
                rel="noreferrer"
                className={"undecorated ml-auto"}
                data-tooltip={meetingLink}
              >
                <Box alignItems={"center"}>
                  <Icon name={"video-camera"} color={"info"} />
                  <span>
                    {t("documents.meetingForm.electronicParticipationLink.join-video-meeting")}{" "}
                  </span>
                </Box>
              </a>
            )}
          </Box>
        </VBox>
        <Box vertical={isMobileView()}>
          {edit || actions?.length ? (
            <Box alignItems="center">
              {edit && <Button label={t("edit")} variant="secondary" onClick={edit} />}
              {actions?.map(({ key, ...props }) => (
                <Button key={key} {...props} />
              ))}
            </Box>
          ) : undefined}
          <Box alignItems="center">
            {ical && googleCalendar && !isCirculation && (
              <AddToCalendar
                title={t("meeting_at", { meeting: title, place: companyName })}
                ical={ical}
                googleCalendar={googleCalendar}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  },
);

export const MatterText = observer(
  ({
    matterText,
    isMultiLanguage,
    isInformationMatter,
  }: {
    matterText: RenderedDecisionRow[];
    isMultiLanguage: boolean;
    isInformationMatter: boolean;
  }) => {
    const { t } = useTranslation();
    const matterHasText =
      matterText.flatMap((m) => m.cells).filter((t) => isNotBlank(t)).length > 0;
    return matterHasText ? (
      <div>
        {!isInformationMatter && (
          <span className={"subtitle"}>{t("meetings.decision.to_review")}</span>
        )}
        <Grid cols={1} colsMd={isMultiLanguage ? 2 : 1}>
          {matterText.map((row, rowNumber) =>
            row.cells.map((cell, column) => (
              <GridItem key={rowNumber + "-" + column} className="content">
                {parse(removeTitleAndTD(cell), { trim: true })}
              </GridItem>
            )),
          )}
        </Grid>
      </div>
    ) : null;
  },
);

export const MatterExtraText = observer(
  ({
    texts,
    documentType,
    isMultiLanguage,
  }: {
    texts: LocalizedTextForDocument[];
    documentType: DocumentType;
    isMultiLanguage: boolean;
  }) => {
    return (
      <Grid cols={1} colsMd={isMultiLanguage ? 2 : 1}>
        {texts
          .filter((x) => x.documentType === documentType)
          .map(({ text, language }) => (
            <Markdown key={language} value={text} />
          ))}
      </Grid>
    );
  },
);

export const Attachments = observer(
  ({
    attachments,
    onDownloadAttachment,
    caption,
  }: {
    attachments: UploadedAttachment[];
    onDownloadAttachment?: (fileId: string) => void;
    caption: TMessage;
  }) => {
    const { t } = useTranslation();
    return attachments && onDownloadAttachment && attachments.length > 0 ? (
      <VBox marginTop>
        <div className={"subtitle"}>{t(caption)}</div>
        <AttachmentsPanel
          uploadedAttachments={attachments}
          onOpen={onDownloadAttachment}
          title={false}
        />
      </VBox>
    ) : null;
  },
);

export const AddNameAndEmailForm: FC<{
  form: { nameField: StringFieldModel; emailField: StringFieldModel };
  submit: () => void;
  cancel: () => void;
}> = observer(({ form, submit, cancel }) => {
  const { t } = useTranslation();
  return (
    <Form submitOnEnter onSubmit={submit} className={"grow"}>
      <VBox>
        <MTextInput autofocus label={t("name")} model={form.nameField} />
        <MTextInput label={t("email")} model={form.emailField} />
        <Box>
          <Button
            variant={"primary"}
            size={"sm"}
            icon={"check"}
            label={t("add")}
            onClick={submit}
          />
          <Button
            size={"sm"}
            icon={"x"}
            label={t("cancel")}
            onClick={cancel}
            variant={"secondary"}
          />
        </Box>
      </VBox>
    </Form>
  );
});

export function SendingTime({ sendingTime }: { sendingTime: string | undefined }) {
  const { t } = useTranslation();
  const locale = useLanguage();
  return sendingTime ? (
    <div className={"subtle text-xs"}>
      {t("sent-at", {
        date: formatDateTime(locale, sendingTime, { relative: true }),
      })}
    </div>
  ) : null;
}

export function LastLogin({ lastLogin }: { lastLogin: string | undefined }): ReactElement {
  const locale = useLanguage();
  const { t } = useTranslation();
  if (lastLogin === undefined) {
    return <Tag color={"neutral"} label={t("never-logged-in")} />;
  } else {
    return <div>{formatDateTime(locale, lastLogin, { relative: true })}</div>;
  }
}

export const SelectField = observer(function MRadioButtonGroup<T>({
  field,
  name,
  label,
  fullWidth,
  isLocked,
  onChange,
}: {
  field: RadioFieldModel<T>;
  name: string;
  label?: string;
  fullWidth?: boolean;
  isLocked?: boolean;
  onChange?: (option: T) => void;
}) {
  return (
    <MSelect
      fullWidth={fullWidth}
      label={label}
      name={name}
      model={field}
      disabled={isLocked}
      onSelect={onChange}
    />
  );
});
export const RadioFieldQuestion = observer(function RadioQuestion<T>({
  question,
  infoText,
  field,
  fieldName,
  row,
  isLocked,
  onChange,
}: {
  question: TMessage;
  infoText?: TMessage;
  field: RadioFieldModel<T>;
  fieldName: string;
  row?: boolean;
  isLocked?: boolean;
  onChange?: (option: T) => void;
}) {
  const { t } = useTranslation();

  return (
    <VBox>
      <div>
        {t(question)}
        &nbsp;
        <InfoButton infoText={when(infoText, t)} />
      </div>
      <MRadioButtonGroup
        disabled={isLocked}
        horizontal={row}
        name={fieldName}
        model={field}
        onChange={(_, option) => (onChange ? onChange(option) : field.setValue(option))}
      />
    </VBox>
  );
});

export type SendForSigningDialogProps = {
  form: SignersForm;
  title: TMessage;
  isSending?: boolean;
  canClose: boolean;
  canSubmit: boolean;
  information?: { icon?: IconName; message: ReactElement | string | number };
  canRemoveSigner?: (signer: SignerModel) => boolean;
  removeSigner?: (signer: SignerModel) => void;
  addSigner?: () => void;
  close: () => void;
  submit: () => void;
  submitButton?: ReactNode;
  showMessageFields?: boolean;
  showUnsupportedAttachmentWarning?: boolean;
  signingCompleteInfoMessage?: ReactElement | string;
  destinationFolderLabel?: TMessageKey;
  documentDestinations?: DocumentStorageDestination[];
  quickAddItems?: QuickAddSignerItems;
  hasSuccessUploadedDocuments?: boolean;
  commonSigningProperties?: CommonSigningProperties;
  preview?: FilePreviewProps;
  documentsForSigning?: DocumentsForPreview;
  companiesForSigning?: CompaniesForPreview;
  enableFullscreenMode?: boolean;
  headerTitle?: TMessage;
};

export const SendForSigningDialog = observer(
  ({
    form,
    title,
    headerTitle,
    isSending,
    canClose,
    canSubmit,
    information,
    canRemoveSigner,
    removeSigner,
    addSigner,
    close,
    submit,
    showMessageFields,
    showUnsupportedAttachmentWarning,
    signingCompleteInfoMessage,
    destinationFolderLabel,
    documentDestinations,
    quickAddItems,
    hasSuccessUploadedDocuments,
    commonSigningProperties,
    preview,
    documentsForSigning,
    companiesForSigning,
    enableFullscreenMode,
    submitButton,
  }: SendForSigningDialogProps) => {
    const { t } = useTranslation();
    return (
      <Fragment>
        {enableFullscreenMode ? (
          <Dialog fullscreen>
            <FullScreenLayout
              centerPanelContainerProps={
                isMobileView() ? undefined : { className: "hidden lg:flex basis-1/2" }
              }
              pageHeader={
                <Box bg borderBottom padding alignItems={"center"}>
                  <IconButton
                    icon={"x"}
                    tooltip={{ key: "close" }}
                    color={"inherit"}
                    onClick={close}
                  />
                  <div>{headerTitle ? t(headerTitle) : t("send-for-signing")}</div>
                </Box>
              }
              centerPanel={
                <HiddenForMobile>
                  {preview && (
                    <VBox grow overflow={"hidden"} gap={false}>
                      <SendDocumentPreviewHeader
                        documents={documentsForSigning}
                        companies={companiesForSigning}
                        title={title}
                      />
                      <VBox paddingX overflow={"auto"} grow>
                        <FilePreview {...preview} width={"auto"} />
                      </VBox>
                    </VBox>
                  )}
                </HiddenForMobile>
              }
              rightPanelContainerProps={
                isMobileView() ? undefined : { className: "basis-1/2 relative grow" }
              }
              rightPanel={
                <SendDocumentForSigningView
                  form={form}
                  showMessageFields={showMessageFields}
                  commonSigningProperties={commonSigningProperties}
                  canRemoveSigner={canRemoveSigner}
                  removeSigner={removeSigner}
                  addSigner={addSigner}
                  quickAddItems={quickAddItems}
                  signingCompleteInfoMessage={signingCompleteInfoMessage}
                  destinationFolderLabel={destinationFolderLabel}
                  documentDestinations={documentDestinations}
                  information={information}
                  hasSuccessUploadedDocuments={hasSuccessUploadedDocuments}
                  showUnsupportedAttachmentWarning={showUnsupportedAttachmentWarning}
                />
              }
              footerPanel={
                <Box bg borderBottom padding border justifyContent={"end"}>
                  <Button
                    variant={"secondary"}
                    label={t("cancel")}
                    onClick={close}
                    disabled={!canClose}
                  />
                  <Button
                    variant={"primary"}
                    label={t("send")}
                    onClick={submit}
                    disabled={!canSubmit && !isSending}
                    loading={isSending}
                  />
                </Box>
              }
            />
          </Dialog>
        ) : (
          <Dialog
            title={t(title)}
            submitLabel={t("send")}
            isSubmitting={isSending}
            isSubmitDisabled={!canSubmit}
            isCancelDisabled={!canClose}
            onSubmit={submit}
            onCancel={close}
            actions={submitButton}
          >
            {form.error ? (
              <Alert type={"error"} message={t(form.error)} />
            ) : information ? (
              <Alert type={hasSuccessUploadedDocuments ? "success" : "info"} {...information} />
            ) : form.isSignerListEmpty ? (
              <Alert type={"error"} message={t("documents.empty-signer-list")} />
            ) : showUnsupportedAttachmentWarning ? (
              <Alert
                message={{
                  key: "documents.send-documents-for-signing.alert.unsupported-attachments",
                }}
                type={"warning"}
              />
            ) : undefined}
            <SendDocumentForSigningView
              form={form}
              showMessageFields={showMessageFields}
              commonSigningProperties={commonSigningProperties}
              canRemoveSigner={canRemoveSigner}
              removeSigner={removeSigner}
              addSigner={addSigner}
              quickAddItems={quickAddItems}
            />
            {showUnsupportedAttachmentWarning && (
              <Alert
                message={{
                  key: "documents.send-documents-for-signing.alert.unsupported-attachments",
                }}
                type={"warning"}
                icon={"warning"}
              />
            )}
          </Dialog>
        )}
      </Fragment>
    );
  },
);

const SendDocumentForSigningView = observer(
  ({
    form,
    showMessageFields,
    commonSigningProperties,
    canRemoveSigner,
    removeSigner,
    addSigner,
    quickAddItems,
    signingCompleteInfoMessage,
    destinationFolderLabel,
    documentDestinations,
    information,
    hasSuccessUploadedDocuments,
    showUnsupportedAttachmentWarning,
  }: {
    form: SignersForm;
    showMessageFields?: boolean;
    commonSigningProperties?: CommonSigningProperties;
    canRemoveSigner?: (signer: SignerModel) => boolean;
    removeSigner?: (signer: SignerModel) => void;
    addSigner?: () => void;
    quickAddItems?: QuickAddSignerItems;
    signingCompleteInfoMessage?: ReactElement | string;
    destinationFolderLabel?: TMessageKey;
    documentDestinations?: DocumentStorageDestination[];
    information?: { icon?: IconName; message: ReactElement | string | number };
    hasSuccessUploadedDocuments?: boolean;
    showUnsupportedAttachmentWarning?: boolean;
  }) => {
    return (
      <VBox container marginTop={"lg"}>
        {form.error ? (
          <Alert type={"error"} message={form.error} />
        ) : information ? (
          <Alert type={hasSuccessUploadedDocuments ? "success" : "info"} {...information} />
        ) : form.isSignerListEmpty ? (
          <Alert type={"error"} message={{ key: "documents.empty-signer-list" }} />
        ) : showUnsupportedAttachmentWarning ? (
          <Alert
            message={{ key: "documents.send-documents-for-signing.alert.unsupported-attachments" }}
            type={"warning"}
          />
        ) : undefined}
        {commonSigningProperties && (
          <>
            {showMessageFields && (
              <EmailSettings form={form} commonSigningProperties={commonSigningProperties} />
            )}
            <RecipientsComponent
              form={form}
              commonSigningProperties={commonSigningProperties}
              canRemoveSigner={canRemoveSigner}
              removeSigner={removeSigner}
            />
          </>
        )}
        {!form.isRecipientsLocked && (
          <AddPersonAndOtherItems addSigner={addSigner} quickAddItems={quickAddItems} />
        )}
        <RecipientsForSealedCopy
          form={form}
          signingCompleteInfoMessage={signingCompleteInfoMessage}
          destinationFolderLabel={destinationFolderLabel}
          documentDestinations={documentDestinations}
        />
        {form.sealedCopyAttachments && (
          <AttachmentsForSealedCopy model={form.sealedCopyAttachments} />
        )}
      </VBox>
    );
  },
);

const EmailSettings = observer(
  ({
    form,
    commonSigningProperties,
  }: {
    form: SignersForm;
    commonSigningProperties: CommonSigningProperties;
  }) => {
    const { t } = useTranslation();
    const en = useTranslation(Language.EN);
    const no = useTranslation(Language.NO);
    useEffect(
      () =>
        form.init(
          commonSigningProperties.languageFieldModel.value === Language.NO
            ? no.t(form.emailTemplate)
            : en.t(form.emailTemplate),
        ),
      [form, en, no, commonSigningProperties.languageFieldModel.value],
    );
    return (
      <VBox gap={"md"}>
        <strong>{t("email")}</strong>
        <Box gap={"lg"} wrap={isMobileView()}>
          <div>{t("customize-message")}</div>
          <MRadioButtonGroup horizontal model={commonSigningProperties.languageFieldModel} />
        </Box>
        <Caption>
          {commonSigningProperties.languageFieldModel.value === Language.NO
            ? no.t({ key: "signing.email-greeting-preview" })
            : en.t({ key: "signing.email-greeting-preview" })}
        </Caption>
        <LocalizedTextEditor model={form.emailBody} />
        {form.showBatchEmailsSettings && (
          <>
            <Box gap={"lg"} wrap={isMobileView()}>
              <div>{t("signing.batch-emails.label")}</div>
              <MRadioButtonGroup model={form.batchEmails} horizontal />
            </Box>
            {!form.dismissedBatchEmailsInfo && (
              <Alert
                type="info"
                message={{ key: "signing.batch-emails.info" }}
                onClose={form.dismissBatchEmailsInfo}
              />
            )}
          </>
        )}
      </VBox>
    );
  },
);
const RecipientsComponent = observer(
  ({
    form,
    commonSigningProperties,
    canRemoveSigner,
    removeSigner,
  }: {
    form: SignersForm;
    commonSigningProperties: CommonSigningProperties;
    canRemoveSigner?: (signer: SignerModel) => boolean;
    removeSigner?: (signer: SignerModel) => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"lg"} marginTop={"md"}>
        <Box
          vertical={isMobileView()}
          justifyContent={"space-between"}
          alignItems={isMobileView() ? "start" : "center"}
        >
          <strong>{t("archive.send-for-signing.send-to")}</strong>
          <Box
            vertical={isMobileView()}
            gap={isMobileView() ? "md" : "xl"}
            alignItems={isMobileView() ? "start" : "center"}
            marginRight={"sm"}
          >
            {t("signatures.send-documents-for-signing.signing-method")}
            <Box>
              <MSelect
                model={commonSigningProperties.signingMethodFieldModel}
                fullWidth
                disabled={commonSigningProperties.isSigningMethodLocked}
              />
            </Box>
          </Box>
        </Box>
        {form.groupedItems.map(({ groupId, label, items }) => (
          <VBox key={groupId} gap={"md"}>
            {form.showGroupHeading(label) && <Box fontWeight={"bold"}>{label}</Box>}
            <Table variant={"IN_CARD"} inline={!isMobileView()}>
              <thead>
                <tr>
                  <th align={"left"} style={{ minWidth: "150px" }}>
                    {t("name")}
                  </th>
                  <th align={"left"} style={{ minWidth: "150px" }}>
                    {t("email")}
                  </th>
                  <th align={"left"}>
                    {t("signatures.send-documents-for-signing.signing-method.custom")}
                  </th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((signer) => {
                  const isSyncedDuplicate = form.isSyncedDuplicate(signer);
                  const isLocked = form.isRecipientsLocked || isSyncedDuplicate;
                  return (
                    <tr key={signer.key}>
                      <td>
                        <Box justifyContent={"space-between"}>
                          <MTextInput
                            placeholder={t("name")}
                            model={signer.nameField}
                            disabled={isLocked}
                            onBlur={() => form.commitAndSyncDuplicates(signer.nameField)}
                            addon={
                              signer.signerType === SignerType.SUBSCRIPTION ? (
                                <Box marginRight={"sm"} alignItems={"center"}>
                                  <Icon name={"certificate"} />
                                </Box>
                              ) : undefined
                            }
                          />
                        </Box>
                      </td>
                      <td>
                        <MTextInput
                          placeholder={t("email")}
                          model={signer.emailField}
                          disabled={isLocked}
                          onBlur={() => form.commitAndSyncDuplicates(signer.emailField)}
                        />
                      </td>
                      <td>
                        <Box>
                          <SelectField
                            name={"signingMethod"}
                            field={signer.methodField}
                            onChange={(option) => {
                              signer.setSigningMethod(option);
                              form.syncDuplicates();
                              commonSigningProperties.changeCommonSigningMethod();
                            }}
                            isLocked={
                              commonSigningProperties.isSigningMethodLocked || isSyncedDuplicate
                            }
                            fullWidth
                          />
                          {!form.isRecipientsLocked &&
                            !isSyncedDuplicate &&
                            canRemoveSigner?.(signer) && (
                              <IconButton
                                tooltip={t("remove")}
                                onClick={(event) => {
                                  event.preventDefault();
                                  removeSigner?.(signer);
                                  commonSigningProperties.changeCommonSigningMethod();
                                }}
                                icon={"trash"}
                                color={"error"}
                              />
                            )}
                        </Box>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </VBox>
        ))}
      </VBox>
    );
  },
);

const RecipientsForSealedCopy = observer(
  ({
    form,
    signingCompleteInfoMessage,
    destinationFolderLabel,
    documentDestinations,
  }: {
    form: SignersForm;
    signingCompleteInfoMessage?: ReactElement | string;
    destinationFolderLabel?: TMessageKey;
    documentDestinations?: DocumentStorageDestination[];
  }) => {
    const { t } = useTranslation();

    return (
      <VBox marginTop={"lg"} marginBottom={"lg"}>
        <strong>{t("signatures.send-documents-for-signing.sealed-copy")}</strong>
        <Grid
          marginBottom={form.isSendSealedCopyToOthersSelected ? "md" : "sm"}
          cols={3}
          gap={"lg"}
        >
          <div> {t("send-for-signing.send-to")}</div>
          <GridItem colSpan={2}>
            <MCheckboxGroup horizontal model={form.sendSealedCopyTo} name={"signingCompleteCopy"} />
          </GridItem>
          {form.isSendSealedCopyToOthersSelected && (
            <GridItem colSpan={3}>
              <SendSealedCopyToOtherRecipients
                model={form.sendSealedCopyToOthers}
                enableRemoveRecipient={form.enableRemoveOtherRecipients}
                removeRecipient={form.removeOtherRecipient}
                addRecipient={form.addOtherRecipient}
              />
            </GridItem>
          )}

          {documentDestinations ? (
            <DocumentStorageDestinations destinations={documentDestinations} />
          ) : (
            <>
              {destinationFolderLabel ? (
                <>
                  <GridItem colSpan={1}>
                    <div> {t("send-for-signing.save-to-archive")}</div>
                  </GridItem>
                  <GridItem colSpan={2}>
                    <Box alignItems="center">
                      <Icon name="folder" />
                      <span>{t(destinationFolderLabel)}</span>
                      <InfoButton infoText={signingCompleteInfoMessage} />
                    </Box>
                  </GridItem>
                </>
              ) : (
                <SaveToArchiveDestination form={form} />
              )}
              {form.sharepointDestination && (
                <>
                  <Box alignItems={"center"}> {t("send-for-signing.save-to-sharepoint")}</Box>
                  <GridItem colSpan={2}>
                    <SharepointDestination model={form.sharepointDestination} />
                  </GridItem>
                </>
              )}
            </>
          )}
        </Grid>
      </VBox>
    );
  },
);

const SharepointDestination = observer(({ model }: { model: SharepointDestinationModel }) => {
  const { t } = useTranslation();
  return (
    <Box alignItems={"center"}>
      {model.isSharepointEnabled ? (
        <ConfigureSharepointDestination model={model} />
      ) : (
        <>
          <LinkButton
            label={t("dbot.select-sharepoint-destination")}
            icon={"folder-open"}
            weight={"normal"}
            disabled
          />
          <InfoButton
            infoText={{
              key: "send-for-signing.save-to-sharepoint.contact-sales",
              params: {
                company: encodeURI(model.sharepointContext.company.name),
                orgNo: model.sharepointContext.company.organizationnumber,
                email: model.sharepointContext.user.email ?? "",
                customerName: encodeURI(formatUserName(model.sharepointContext.user)) ?? "",
              },
            }}
          />
        </>
      )}
    </Box>
  );
});

const DocumentArchiveDestinationView = observer(
  ({ archive }: { archive: DocumentArchiveDestination }) => {
    const { t } = useTranslation();
    return archive.type === "fixed" ? (
      <Box alignItems={"center"}>
        <Icon name={"folder"} />
        <span>{t(archive.label)}</span>
      </Box>
    ) : (
      <MSelect model={archive.folderModel} />
    );
  },
);

const DocumentStorageDestinations = observer(
  ({ destinations }: { destinations: DocumentStorageDestination[] }) => {
    const { t } = useTranslation();
    const archiveDestinations = destinations.flatMap((d) =>
      d.archive ? [{ ...d, archive: d.archive }] : [],
    );
    const [single] = destinations;
    if (destinations.length === 1) {
      return (
        <>
          {single.archive && (
            <>
              <div>{t("send-for-signing.save-to-archive")}</div>
              <GridItem colSpan={2}>
                <DocumentArchiveDestinationView archive={single.archive} />
              </GridItem>
            </>
          )}
          <div>{t("send-for-signing.save-to-sharepoint")}</div>
          <GridItem colSpan={2}>
            <SharepointDestination model={single.sharepoint} />
          </GridItem>
        </>
      );
    }
    return (
      <>
        {archiveDestinations.length > 0 && (
          <>
            <GridItem colSpan={3}>{t("send-for-signing.save-to-archive")}</GridItem>
            {archiveDestinations.map((d) => (
              <Fragment key={d.documentId}>
                <GridItem tooltip={d.title} className={"truncate self-center"}>
                  {d.title}
                </GridItem>
                <GridItem colSpan={2}>
                  <DocumentArchiveDestinationView archive={d.archive} />
                </GridItem>
              </Fragment>
            ))}
          </>
        )}
        <GridItem colSpan={3}>{t("send-for-signing.save-to-sharepoint")}</GridItem>
        {destinations.map((d) => (
          <Fragment key={d.documentId}>
            <GridItem tooltip={d.title} className={"truncate self-center"}>
              {d.title}
            </GridItem>
            <GridItem colSpan={2}>
              <SharepointDestination model={d.sharepoint} />
            </GridItem>
          </Fragment>
        ))}
      </>
    );
  },
);

const AttachmentsForSealedCopy = observer(
  ({ model }: { model: OptionFieldModel<SealedCopyAttachment> }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"sm"} marginBottom={"lg"}>
        <strong>{t("send-for-signing.sealed-copy-attachments.title")}</strong>
        <Box alignItems={"center"}>
          <span>{t("send-for-signing.sealed-copy-attachments.description")}</span>
          <InfoButton infoText={{ key: "send-for-signing.sealed-copy-attachments.info" }} />
        </Box>
        {model.options.length > 1 && (
          <Box>
            <LinkButton
              label={t(model.isAllSelected ? "select-none" : "select-all")}
              onClick={() => model.toggleAll(!model.isAllSelected)}
            />
          </Box>
        )}
        <VBox gap={"sm"}>
          {model.options.map((attachment) => (
            <Box
              key={model.getId(attachment)}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <div className={"shrink-0"}>
                <Checkbox
                  name={"sealedCopyAttachments"}
                  label={attachment.filename}
                  checked={model.isSelected(attachment)}
                  onChange={() => model.toggle(attachment)}
                />
              </div>
              {attachment.documentTitle && (
                <span className={"subtle truncate min-w-0"} title={attachment.documentTitle}>
                  {t({
                    key: "send-for-signing.sealed-copy-attachments.attached-to",
                    params: { document: attachment.documentTitle },
                  })}
                </span>
              )}
            </Box>
          ))}
        </VBox>
      </VBox>
    );
  },
);

const SendSealedCopyToOtherRecipients = observer(
  ({
    model,
    enableRemoveRecipient,
    removeRecipient,
    addRecipient,
  }: {
    model: SendSealedCopyToOtherModel[];
    enableRemoveRecipient: boolean;
    removeRecipient: (recipient: SendSealedCopyToOtherModel) => void;
    addRecipient: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"md"}>
        <Table variant={"IN_CARD"} inline={!isMobileView()}>
          <thead>
            <tr>
              <th align={"left"} style={{ minWidth: "150px" }}>
                {t("name")}
              </th>
              <th align={"left"} style={{ minWidth: "150px" }}>
                {t("email")}
              </th>
            </tr>
          </thead>
          <tbody>
            {model.map((recipient) => (
              <tr key={recipient.id}>
                <td className={"align-top"}>
                  <MTextInput placeholder={t("name")} model={recipient.name} />
                </td>
                <td className={"align-top"}>
                  <MTextInput placeholder={t("email")} model={recipient.email} />
                </td>
                <td className={"align-top"}>
                  {enableRemoveRecipient && (
                    <IconButton
                      tooltip={t("remove")}
                      onClick={(event) => {
                        event.preventDefault();
                        removeRecipient?.(recipient);
                      }}
                      icon={"trash"}
                      color={"error"}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
        <Box>
          <LinkButton icon={"plus"} label={{ key: "addOtherRecipients" }} onClick={addRecipient} />
        </Box>
      </VBox>
    );
  },
);

const AddPersonAndOtherItems = observer(
  ({
    addSigner,
    quickAddItems,
  }: {
    addSigner?: () => void;
    quickAddItems?: QuickAddSignerItems;
  }) => {
    const { t } = useTranslation();

    return (
      <Fragment>
        {(Boolean(addSigner) || (quickAddItems && quickAddItems.length > 0)) && (
          <VBox gap={"lg"}>
            {Boolean(addSigner) && (
              <Box>
                <LinkButton
                  label={t("addPerson")}
                  icon={"plus"}
                  onClick={(event) => {
                    event.preventDefault();
                    addSigner?.();
                  }}
                />
              </Box>
            )}
            {quickAddItems && quickAddItems.length > 0 && (
              <VBox gap={"md"}>
                {quickAddItems.map(
                  (row) =>
                    row.items.length > 0 && (
                      <Box wrap key={row.rowId}>
                        {row.items.map((q) => (
                          <Dismissable
                            key={q.id}
                            onDismiss={q.dismiss}
                            tooltip={{ key: "send-for-signing.dont-suggest-again" }}
                          >
                            <ToggleButton
                              testId={q.id.toString()}
                              icon={"plus"}
                              variant={"small"}
                              label={t(q.label)}
                              onClick={preventDefault(q.action)}
                            />
                          </Dismissable>
                        ))}
                      </Box>
                    ),
                )}
              </VBox>
            )}
          </VBox>
        )}
      </Fragment>
    );
  },
);

export const SendDocumentPreviewHeader = observer(
  ({
    documents,
    companies,
    title,
  }: {
    documents?: DocumentsForPreview;
    companies?: CompaniesForPreview;
    title?: TMessage;
  }) => {
    const { t } = useTranslation();
    return (
      <Grid bg borderBottom padding alignItems={"center"} gap={"md"}>
        {companies && companies.companies.length > 1 && (
          <GridItem xs={6}>
            <Box grow>
              <Button
                variant={"basic"}
                icon={"caret-up"}
                tooltip={t("previous")}
                onClick={companies.previousCompany}
                disabled={!companies.previousCompany}
              />
              <Button
                variant={"basic"}
                icon={"caret-down"}
                tooltip={t("next")}
                onClick={companies.nextCompany}
                disabled={!companies.nextCompany}
              />
              <Select
                icon={"buildings"}
                value={companies.selectedCompany}
                items={companies.companies}
                onSelect={companies.selectCompany}
                idFn={(company) => company.organisationNumber}
                labelFn={(company) => company.companyName}
              />
            </Box>
          </GridItem>
        )}
        {documents ? (
          <GridItem xs={companies && companies.companies.length > 1 ? 6 : 12}>
            {(companies && companies.companies.length > 1) || documents.documents.length > 1 ? (
              <Box grow>
                <Select
                  icon={"file-text"}
                  value={documents.selectedDocument}
                  items={documents.documents}
                  onSelect={documents.selectDocument}
                  idFn={(document) => document.id + document?.reference}
                  labelFn={(document) => document.name}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-left"}
                  tooltip={t("previous")}
                  onClick={documents.previousDocument}
                  disabled={!documents.previousDocument}
                />
                <Button
                  variant={"basic"}
                  icon={"caret-right"}
                  tooltip={t("next")}
                  onClick={documents.nextDocument}
                  disabled={!documents.nextDocument}
                />
              </Box>
            ) : (
              <Box>
                <Icon name={"file-text"} />
                <div
                  className={"truncate"}
                  data-tooltip={
                    companies && companies.companies.length > 1
                      ? documents.selectedDocument?.name
                      : undefined
                  }
                >
                  {documents.selectedDocument?.name}
                </div>
              </Box>
            )}
          </GridItem>
        ) : title ? (
          <GridItem xs={12}>
            <Box>
              <Icon name={"file-text"} />
              <div className={"truncate"}>{t(title)}</div>
            </Box>
          </GridItem>
        ) : undefined}
      </Grid>
    );
  },
);

export const ConfirmDeleteSigningRequestDialog = observer(
  ({
    isSubmitting,
    onConfirm,
    onCancel,
  }: {
    isSubmitting?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
  }) => {
    const { t } = useTranslation();
    return (
      <Dialog
        maxWidth={"sm"}
        title={t("archive.delete-signing-request.title")}
        onCancel={onCancel}
        onSubmit={onConfirm}
        submitLabel={getActionLabel("delete-signing-request")}
        isSubmitting={isSubmitting}
        submitColor={"error"}
      >
        <VBox gap={"lg"}>
          <p>{t("archive.delete-signing-request.description")}</p>
        </VBox>
      </Dialog>
    );
  },
);

const SaveToArchiveDestination = observer(({ form }: { form: SignersForm }) => {
  const { t } = useTranslation();
  if (
    !form.showDestinationFolderSelector ||
    (form.isBatchSigning && !form.batchDestinationFolderModels)
  ) {
    return null;
  }

  return (
    <>
      <GridItem colSpan={1}>
        <div> {t("send-for-signing.save-to-archive")}</div>
      </GridItem>
      <GridItem colSpan={2}>
        {form.savedFolderMode !== undefined ? (
          <Box gap={"md"}>
            <Box alignItems={"center"}>
              <Icon name={"folder-open"} />
              {form.savedFolderMode === DestinationFolderMode.PER_COMPANY_FOLDER ? (
                <i>{t("dbot.destination-folder.multiple-companies")}</i>
              ) : form.selectedDestinationFolder ? (
                <div>
                  {t("dbot.destination-folder.single-company", {
                    folderName: t(getEntryLabel(form.selectedDestinationFolder)),
                    companyName: form.companiesItemsModel?.value?.companyName,
                  })}
                </div>
              ) : null}
            </Box>
            <LinkButton
              label={t("edit")}
              icon={"folder-open"}
              onClick={form.openDestinationFolderDialog}
            />
            <LinkButton
              label={t("reset")}
              icon={"arrow-counter-clockwise"}
              onClick={form.resetDestinationFolder}
            />
          </Box>
        ) : form.selectedDestinationFolder ? (
          <Box gap={"lg"}>
            <Box>
              <Icon name={"folder-open"} />
              {t(getEntryLabel(form.selectedDestinationFolder))}
            </Box>
            <LinkButton
              label={t("edit")}
              icon={"folder-open"}
              onClick={form.openDestinationFolderDialog}
            />
            <LinkButton
              label={t("reset")}
              icon={"arrow-counter-clockwise"}
              onClick={form.resetDestinationFolder}
            />
          </Box>
        ) : (
          <LinkButton
            icon={"folder-open"}
            label={t("dbot.select-destination")}
            onClick={form.openDestinationFolderDialog}
          />
        )}
      </GridItem>
      {form.showDestinationFolderDialog && (
        <Dialog
          title={t("signatures.send-documents-for-signing.destination-folder")}
          submitLabel={t("save")}
          cancelLabel={t("cancel")}
          onCancel={form.onCancelDestinationFolderDialog}
          onSubmit={form.onSaveDestinationFolder}
        >
          {form.batchDestinationFolderModels && (
            <BatchDestinationFolder {...form.batchDestinationFolderModels} />
          )}

          {form.destinationFolderModel && !form.isBatchSigning && (
            <MSelect model={form.destinationFolderModel} label={t("dbot.destination-folder")} />
          )}
        </Dialog>
      )}
    </>
  );
});

const CompanyFolderRow = observer(
  ({ company, entry }: { company: CompanyItem; entry?: CompanyFolderEntry }) => {
    const destinationFolder =
      !entry || entry.isLoading ? (
        <LoadingIndicator size={"sm"} />
      ) : entry.folderModel ? (
        <MSelect model={entry.folderModel} />
      ) : null;

    return (
      <>
        <Box alignItems={"center"} gap={"sm"}>
          <Icon name={"buildings"} />
          <div>{company.companyName}</div>
        </Box>
        <div>{destinationFolder}</div>
      </>
    );
  },
);

const BatchDestinationFolder = observer(
  ({
    companyFieldModel,
    singleDestinationFolderModel,
    folderMode,
    multipleDestinationFolderModels,
  }: {
    companyFieldModel: RadioFieldModel<CompanyItem>;
    singleDestinationFolderModel?: RadioFieldModel<DestinationFolder | undefined>;
    folderMode: RadioFieldModel<DestinationFolderMode>;
    multipleDestinationFolderModels: CompanyFolderEntry[];
  }) => {
    const { t } = useTranslation();
    return (
      <VBox gap={"md"}>
        <div>{t("dbot.destination-folder.portfolio-projects.description")}</div>
        <MRadioButtonGroup model={folderMode} horizontal />
        {folderMode.value === DestinationFolderMode.SINGLE_FOLDER ? (
          <Box gap={"md"}>
            <MSelect model={companyFieldModel} label={t("company")} />
            {singleDestinationFolderModel && (
              <MSelect model={singleDestinationFolderModel} label={t("dbot.destination-folder")} />
            )}
          </Box>
        ) : (
          <Grid cols={2} gap={"sm"} alignItems={"center"} marginTop={"md"}>
            <div>{t("company")}</div>
            <div>{t("dbot.destination-folder")}</div>
            {companyFieldModel.options.map((company) => (
              <CompanyFolderRow
                key={company.organisationNumber}
                company={company}
                entry={multipleDestinationFolderModels.find(
                  (e) => e.company.organisationNumber === company.organisationNumber,
                )}
              />
            ))}
          </Grid>
        )}
      </VBox>
    );
  },
);

export const DocumentRecipients = observer(
  ({
    recipients,
    alert,
    isSendingDialog,
    dynamicRecipients,
    isReadOnly,
    sentRecipientIds,
  }: {
    recipients: RecipientsEditModel;
    alert?: AlertProps;
    isSendingDialog?: boolean;
    dynamicRecipients?: DynamicRecipientOption[];
    isReadOnly?: boolean;
    sentRecipientIds?: ReadonlySet<string>;
  }) => {
    const { t } = useTranslation();
    return recipients.isEmpty ? (
      <div>{t("none")}</div>
    ) : (
      <VBox>
        {!isSendingDialog &&
          dynamicRecipients
            ?.filter((d) => recipients.hasDynamicRecipient(d.type))
            .map((d) => (
              <Box
                key={d.type}
                bg
                hoverBg
                rounded
                border
                alignItems={"center"}
                paddingLeft={"md"}
                paddingRight={"sm"}
                paddingY={"sm"}
              >
                <Icon name={"users"} />
                {t(dynamicRecipientLabels[d.type], {
                  count: d.count,
                })}
                {!isReadOnly && (
                  <Box marginLeft={"auto"}>
                    <IconButton
                      icon={"x"}
                      tooltip={t("delete")}
                      color="gray"
                      onClick={stopPropagation(() => recipients.removeDynamicRecipient(d.type))}
                    />
                  </Box>
                )}
              </Box>
            ))}
        {recipients.recipientFields.map((r) => {
          const alreadySent = sentRecipientIds?.has(String(r.id)) ?? false;
          return (
            <Box key={r.id} alignItems="start">
              <MTextInput
                model={r.nameField}
                placeholder={t("name")}
                autofocus
                disabled={isReadOnly || !recipients.canEditRecipientName(r)}
              />
              <MTextInput model={r.emailField} placeholder={t("email")} disabled={isReadOnly} />
              <IconButton
                icon={"trash"}
                tooltip={{ key: "delete" }}
                color={"error"}
                disabled={isReadOnly || alreadySent}
                onClick={() => recipients.removeRecipient(r.id)}
              />
            </Box>
          );
        })}
        {when(alert, (props) => (
          <Alert {...props} />
        ))}
      </VBox>
    );
  },
);

export const SendDocumentForViewingDialog = observer(
  ({ model }: { model: SendDocumentForViewingModel }) => {
    const { t } = useTranslation();
    const isIndividualised = model.isDocumentIndividualised && model.documents.length > 1;
    return (
      <Dialog fullscreen>
        <FullScreenLayout
          centerPanelContainerProps={
            isMobileView() ? undefined : { className: "hidden lg:flex basis-1/2" }
          }
          pageHeader={
            <Box bg borderBottom padding alignItems={"center"}>
              <IconButton
                icon={"x"}
                tooltip={{ key: "close" }}
                color={"inherit"}
                onClick={model.onClose}
              />
              <div>{t("dbot.send-document.title")}</div>
            </Box>
          }
          centerPanel={
            <HiddenForMobile>
              <VBox grow overflow={"hidden"} gap={false}>
                <SendDocumentPreviewHeader
                  documents={model.documentsForPreview}
                  companies={model.companiesForPreview}
                  title={model.title}
                />
                <VBox paddingX overflow={"auto"} grow>
                  {model.filePreview && <FilePreview {...model.filePreview} width={"auto"} />}
                </VBox>
              </VBox>
            </HiddenForMobile>
          }
          rightPanelContainerProps={
            isMobileView() ? undefined : { className: "basis-1/2 relative grow" }
          }
          rightPanel={
            <VBox container marginTop={"lg"} gap={"md"}>
              <h2>{t("dbot.send-document.title")}</h2>
              {isIndividualised && (
                <Alert type={"info"} message={t("dbot.send-document.info.individualised")} />
              )}
              <LocalizedTextEditor
                model={model.emailBody}
                placeholder={{ key: "customize-message.placeholder" }}
              />
              <VBox>
                <div>{t("dbot.recipients")}</div>
                {model.recipientsAlert && <Alert {...model.recipientsAlert} />}
                {Object.entries(model.recipientsByCompany).map(
                  ([orgNo, recipients], _index, entries) => (
                    <VBox key={orgNo} marginBottom>
                      {entries.length > 1 && (
                        <strong>
                          {model.companies.find((c) => c.organisationNumber === orgNo)?.companyName}
                        </strong>
                      )}
                      <DocumentRecipients recipients={recipients} isSendingDialog />
                    </VBox>
                  ),
                )}
              </VBox>
            </VBox>
          }
          footerPanel={
            <Box bg borderBottom padding border justifyContent={"end"}>
              <Button variant={"secondary"} label={t("cancel")} onClick={model.onClose} />
              <Button
                variant={"primary"}
                label={t("send")}
                onClick={model.onSubmit}
                loading={model.isSubmitting}
              />
            </Box>
          }
        />
      </Dialog>
    );
  },
);

export type AccessTypeTagProps = {
  accessType: AccessType;
  customRole?: CustomRole;
};

export const AccessTypeTag: FC<AccessTypeTagProps> = ({ accessType, customRole }) => {
  const language = useLanguage();
  return (
    <Tag
      color={accessColors[accessType]}
      label={accessTypeLabel(accessType, customRole, language)}
    />
  );
};
